/**
 * Список пользователей с агрегатами + быстрый промоушн/демоушн.
 * Mobile: переключение из Table в Stack of Cards через useMediaQuery.
 */

import {
    ActionIcon, Alert, Badge, Card, Center, Group, Loader, Menu, Stack, Table,
    Text, TextInput, Title,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
    fetchAdminUsers, setUserRole, type UserListItem, type UserRole,
} from '../../lib/api-admin';
import { DESKTOP_QUERY } from '../../lib/breakpoints';
import { fmtDate, fmtNum, fmtRelative } from './format';

export function AdminUsersPage(): JSX.Element {
    const [search, setSearch] = useState('');
    const [roleFilter, setRoleFilter] = useState<UserRole | undefined>(undefined);
    const isDesktop = useMediaQuery(DESKTOP_QUERY);
    const qc = useQueryClient();

    const q = useQuery({
        queryKey: ['admin-users', search, roleFilter],
        queryFn:  () => fetchAdminUsers({ search: search || undefined, role: roleFilter, limit: 200 }),
        retry:    false,
    });

    const m = useMutation({
        mutationFn: ({ email, role }: { email: string; role: UserRole }) => setUserRole(email, role),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
    });

    return (
        <Stack gap="md">
            <Title order={2}>Пользователи</Title>
            <Group gap="sm" wrap="wrap">
                <TextInput
                    value={search}
                    onChange={e => setSearch(e.currentTarget.value)}
                    placeholder="Поиск по email/имени"
                    flex={{ base: '1 0 100%', sm: 1 }}
                />
                <Menu shadow="md">
                    <Menu.Target>
                        <ActionIcon variant="default" size="lg">
                            <Badge size="sm" variant="light">{roleFilter ?? 'все'}</Badge>
                        </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                        <Menu.Item onClick={() => setRoleFilter(undefined)}>Все</Menu.Item>
                        <Menu.Item onClick={() => setRoleFilter('admin')}>Только админы</Menu.Item>
                        <Menu.Item onClick={() => setRoleFilter('user')}>Только обычные</Menu.Item>
                    </Menu.Dropdown>
                </Menu>
            </Group>

            {q.isLoading && <Center my="lg"><Loader /></Center>}
            {q.error && <Alert color="red">{(q.error as Error).message}</Alert>}
            {q.data && (
                <>
                    <Text fz="sm" c="dimmed">Найдено: {q.data.total}</Text>
                    {isDesktop
                        ? <DesktopTable users={q.data.users} onRoleChange={(email, role) => m.mutate({ email, role })} />
                        : <MobileList   users={q.data.users} onRoleChange={(email, role) => m.mutate({ email, role })} />
                    }
                </>
            )}
        </Stack>
    );
}

function DesktopTable({ users, onRoleChange }: { users: UserListItem[]; onRoleChange: (email: string, role: UserRole) => void }): JSX.Element {
    return (
        <Card withBorder padding={0}>
            <Table highlightOnHover striped fz="sm" verticalSpacing="sm">
                <Table.Thead>
                    <Table.Tr>
                        <Table.Th>Email</Table.Th>
                        <Table.Th>Имя</Table.Th>
                        <Table.Th>Роль</Table.Th>
                        <Table.Th>Сессий</Table.Th>
                        <Table.Th>Событий</Table.Th>
                        <Table.Th>Активность</Table.Th>
                        <Table.Th>Первый вход</Table.Th>
                        <Table.Th />
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {users.map(u => (
                        <Table.Tr key={u.email}>
                            <Table.Td><Link to={`/admin/users/${encodeURIComponent(u.email)}`}>{u.email}</Link></Table.Td>
                            <Table.Td>{u.name ?? '—'}</Table.Td>
                            <Table.Td><RoleBadge role={u.role} /></Table.Td>
                            <Table.Td>{fmtNum(u.sessionsCount)}</Table.Td>
                            <Table.Td>{fmtNum(u.eventsCount)}</Table.Td>
                            <Table.Td><Text fz="xs">{fmtRelative(u.lastActivity ?? u.lastSeen)}</Text></Table.Td>
                            <Table.Td><Text fz="xs">{fmtDate(u.firstSeen)}</Text></Table.Td>
                            <Table.Td><RoleMenu email={u.email} role={u.role} onRoleChange={onRoleChange} /></Table.Td>
                        </Table.Tr>
                    ))}
                </Table.Tbody>
            </Table>
        </Card>
    );
}

function MobileList({ users, onRoleChange }: { users: UserListItem[]; onRoleChange: (email: string, role: UserRole) => void }): JSX.Element {
    return (
        <Stack gap="xs">
            {users.map(u => (
                <Card withBorder padding="sm" key={u.email}>
                    <Group justify="space-between" align="start" wrap="nowrap">
                        <div style={{ minWidth: 0, flex: 1 }}>
                            <Text fw={500} truncate>
                                <Link to={`/admin/users/${encodeURIComponent(u.email)}`}>{u.email}</Link>
                            </Text>
                            {u.name && <Text fz="xs" c="dimmed" truncate>{u.name}</Text>}
                        </div>
                        <Group gap={4} wrap="nowrap">
                            <RoleBadge role={u.role} />
                            <RoleMenu email={u.email} role={u.role} onRoleChange={onRoleChange} />
                        </Group>
                    </Group>
                    <Group gap="xs" mt="xs" fz="xs" c="dimmed">
                        <Text fz="xs">сессий {fmtNum(u.sessionsCount)}</Text>
                        <Text fz="xs">·</Text>
                        <Text fz="xs">событий {fmtNum(u.eventsCount)}</Text>
                        <Text fz="xs">·</Text>
                        <Text fz="xs">{fmtRelative(u.lastActivity ?? u.lastSeen)}</Text>
                    </Group>
                </Card>
            ))}
        </Stack>
    );
}

function RoleBadge({ role }: { role: UserRole }): JSX.Element {
    return <Badge color={role === 'admin' ? 'indigo' : 'gray'} variant="light">{role}</Badge>;
}

function RoleMenu({ email, role, onRoleChange }: { email: string; role: UserRole; onRoleChange: (email: string, role: UserRole) => void }): JSX.Element {
    const target: UserRole = role === 'admin' ? 'user' : 'admin';
    const label = target === 'admin' ? 'Сделать админом' : 'Снять с админа';
    return (
        <Menu shadow="md" position="bottom-end">
            <Menu.Target>
                <ActionIcon variant="subtle" aria-label="actions">⋯</ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
                <Menu.Item onClick={() => {
                    if (confirm(`${label}: ${email}?`)) onRoleChange(email, target);
                }}>{label}</Menu.Item>
            </Menu.Dropdown>
        </Menu>
    );
}
